// src/router.jsx
import { createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import RootLayout from './layouts/RootLayout';

// Lazily load all page components
const Home = lazy(() => import('./pages/Home'));
const SignUp = lazy(() => import('./pages/SignUp'));
const NotFound = lazy(() => import('./pages/NotFound'));
const CategoryDetail = lazy(() => import('./pages/CategoryDetail'));
const Experts = lazy(() => import('./pages/Experts'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const LoginCallback = lazy(() => import('./pages/LoginCallback'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const SignIn = lazy(() => import('./pages/SignIn'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Features = lazy(() => import('./pages/Features'));
const Pricing = lazy(() => import('./pages/Pricing'));
const AuthTest = lazy(() => import('./components/AuthTest'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const VerificationSuccess = lazy(() => import('./pages/VerificationSuccess'));
const ResendVerification = lazy(() => import('./pages/ResendVerification'));
const CheckEmailVerification = lazy(() =>
  import('./pages/CheckEmailVerification')
);
const ExpertProfile = lazy(() => import('./pages/ExpertProfile'));
const CreateExpertProfile = lazy(() => import('./pages/CreateExpertProfile'));
const EditExpertProfile = lazy(() => import('./pages/EditExpertProfile'));
const VideoCall = lazy(() => import('./pages/VideoCall'));
const CallFeedback = lazy(() => import('./pages/CallFeedback'));
const HostSessions = lazy(() => import('./pages/HostSessions'));
const GuestSessions = lazy(() => import('./pages/GuestSessions'));
const BookingSuccess = lazy(() => import('./pages/BookingSuccess'));
const BookingCancel = lazy(() => import('./pages/BookingCancel'));
const StripeReturn = lazy(() => import('./pages/StripeReturn'));
const StripeRefresh = lazy(() => import('./pages/StripeRefresh'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

//const SessionPage = lazy(() => import('./pages/SessionPage'));

//import { isLoggedIn } from "./lib/authUtils";

// Protected Route wrapper component
/* const ProtectedRoute = ({ children }) => {
  if (!isLoggedIn()) {
    return <Navigate to="/login" />;
  }
  return children;
}; */

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Wrapped component with Suspense
const withSuspense = (Component) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

const router = createBrowserRouter([
  // All routes with RootLayout
  {
    path: '/',
    element: <RootLayout />,
    errorElement: withSuspense(NotFound),
    children: [
      {
        index: true,
        element: withSuspense(Home),
      },
      {
        path: 'signin',
        element: withSuspense(SignIn),
      },
      {
        path: 'forgot-password',
        element: withSuspense(ForgotPassword),
      },
      {
        path: 'reset-password/:token',
        element: withSuspense(ResetPassword),
      },
      {
        path: 'signup',
        element: withSuspense(SignUp),
      },
      {
        path: 'login-callback',
        element: withSuspense(LoginCallback),
      },
      {
        path: 'user-profile',
        element: withSuspense(UserProfile),
      },
      {
        path: 'features',
        element: withSuspense(Features),
      },
      {
        path: 'pricing',
        element: withSuspense(Pricing),
      },
      { path: '/category/:id', element: withSuspense(CategoryDetail) },
      { path: '/experts', element: withSuspense(Experts) },
      { path: '/privacy-policy', element: withSuspense(PrivacyPolicy) },
      { path: '/terms-of-service', element: withSuspense(TermsOfService) },
      { path: '/auth-test', element: withSuspense(AuthTest) },
      { path: '/verify-email/:token', element: withSuspense(VerifyEmail) },
      {
        path: '/verification-success',
        element: withSuspense(VerificationSuccess),
      },
      {
        path: '/resend-verification',
        element: withSuspense(ResendVerification),
      },
      {
        path: '/check-email-verification',
        element: withSuspense(CheckEmailVerification),
      },
      { path: '/expert/:slug', element: withSuspense(ExpertProfile) },
      { path: '/expert-profile/:id', element: withSuspense(ExpertProfile) }, // Backward compatibility
      {
        path: '/create-expert-profile',
        element: withSuspense(CreateExpertProfile),
      },
      {
        path: '/edit-expert/:slug',
        element: withSuspense(EditExpertProfile),
      },
      {
        path: '/edit-expert-profile/:id',
        element: withSuspense(EditExpertProfile),
      },
      { path: '/call-feedback', element: withSuspense(CallFeedback) },
      { path: '/host/sessions', element: withSuspense(HostSessions) },
      { path: '/my-sessions', element: withSuspense(GuestSessions) },
      { path: '/booking/success', element: withSuspense(BookingSuccess) },
      { path: '/booking/cancel', element: withSuspense(BookingCancel) },
      {
        path: '/host/stripe/return/:accountId',
        element: withSuspense(StripeReturn),
      },
      {
        path: '/host/stripe/refresh/:accountId',
        element: withSuspense(StripeRefresh),
      },
      {
        path: '/admin',
        element: withSuspense(AdminDashboard),
      },
      // VideoCall route
      {
        path: '/session/:bookingId',
        element: withSuspense(VideoCall),
      },
    ],
  },
  // Fullscreen VideoCall route (without RootLayout)
  {
    path: '/session/:bookingId/fullscreen',
    element: withSuspense(VideoCall),
  },
]);

export default router;
